import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Dimensions,
  Platform,
  Image,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { parseDimension } from '../util.style';

interface MenuItem {
  id: string;
  text: string;
  href?: string;
}

interface MenuSection {
  id: string;
  title: string;
  items: MenuItem[];
}

interface LegalLink {
  text: string;
  href: string;
}

interface BottomContent {
  logo: {
    text?: string;
    imageUrl?: string;
  };
  copyright: {
    text?: string;
    year?: number;
    companyName?: string;
  };
  legalLinks: LegalLink[];
}

interface FooterLayoutProps {
  menuSections?: MenuSection[];
  bottomContent?: BottomContent;
  containerStyle?: any;
  screenType?: string;
  screenSize?: { width: number; height: number };
  width?: number;
  height?: number;

  // Live mode props
  attributes?: Record<string, any>;
  style?: any;
  libraryComponentManifest?: any;
  onMenuItemPress?: (item: MenuItem) => void;
  onLegalLinkPress?: (link: LegalLink) => void;

  // Mode detection
  isCanvasMode?: boolean;

  [key: string]: any;
}

const FooterLayout: React.FC<FooterLayoutProps> = (props) => {
  const {
    attributes,
    menuSections: propMenuSections,
    bottomContent: propBottomContent,
    containerStyle,
    screenSize,
    width: canvasWidth,
    height: canvasHeight,
    onMenuItemPress,
    onLegalLinkPress,
    isCanvasMode = false,
    fontFamily,
    ...otherProps
  } = props;

  const [screenDimensions, setScreenDimensions] = useState(() =>
    isCanvasMode && screenSize ? screenSize : Dimensions.get('window')
  );

  // State for accordion functionality in mobile
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (!isCanvasMode) {
      const subscription = Dimensions.addEventListener(
        'change',
        ({ window }) => {
          setScreenDimensions(window);
        }
      );
      return () => subscription?.remove();
    }
  }, [isCanvasMode]);

  const effectiveWidth = isCanvasMode
    ? parseDimension(canvasWidth) ||
      parseDimension(screenSize?.width) ||
      parseDimension(containerStyle?.width) ||
      1200
    : screenDimensions.width;

  const widthNumber =
    typeof effectiveWidth === 'number' ? effectiveWidth : 1200;
  const isDesktop = widthNumber >= 1200;
  const isTablet = widthNumber >= 768 && widthNumber < 1200;
  const isMobile = widthNumber < 768;

  // Get data from props or attributes
  const menuListItems = Array.isArray(attributes?.menuList?.items)
    ? attributes.menuList.items
    : Array.isArray(propMenuSections)
    ? propMenuSections
    : Array.isArray(attributes?.menuSections)
    ? attributes.menuSections
    : [];

  // Process menu items to handle groups only
  const processedGroupMenuItems = menuListItems
    .filter(
      (item: { type: string; children: any }) =>
        item.type === 'group' && Array.isArray(item.children)
    )
    .map((item: { type: string; children: any; id: any; text: any }) => ({
      id: item.id,
      title: item.text,
      items: item.children,
    }));

  const processedMenuItemsOnly = menuListItems
    .filter(
      (item: { type: string; children: any }) =>
        item.type !== 'group' && !Array.isArray(item.children)
    )
    .map((item: { type: string; children: any; id: any; text: any }) => ({
      id: item.id,
      title: item.text,
      items: [],
    }));

  // Map bottom content from attributes child components
  const bottomContent = propBottomContent || {
    copyright: {
      text: attributes?.bottomContent?.copyrightText || '© Company Inc. 2025',
    },
    legalLinks: attributes?.legalLinks?.links || [
      { text: '運営会社', href: '#company' },
      { text: 'プライバシーポリシー', href: '#privacy' },
    ],
  };

  // Get logo data from attributes or bottomContent (matching Header pattern)
  const { logo } = attributes || {};
  const { imageUrl, imageLogo } = logo ?? {};

  // Handle brand logo URL extraction
  let logoImage = '';
  if (imageLogo?.binding?.url) {
    logoImage = imageLogo.binding.url;
  } else if (imageUrl) {
    logoImage = imageUrl;
  } else if (typeof imageLogo === 'string') {
    // Direct string URL
    logoImage = imageLogo;
  }

  const logoData = {
    text: attributes?.logo?.logoText || attributes?.logo?.text || 'LOGO',
    imageUrl: logoImage,
  };

  // Responsive dimensions
  const getResponsiveContainerPaddingHorizontal = () => {
    if (isDesktop) return 40;
    if (isTablet) return 30;
    return 10;
  };

  const getResponsiveContainerPaddingVertical = () => {
    if (isDesktop) return 24;
    if (isTablet) return 16;
    return 10;
  };

  const getResponsiveGridGap = () => {
    if (isDesktop) return 32;
    if (isTablet) return 24;
    return 16;
  };

  const handleMenuItemPress = (item: MenuItem) => {
    if (!isCanvasMode && onMenuItemPress) {
      onMenuItemPress(item);
    }
  };

  const handleLegalLinkPress = (link: LegalLink) => {
    if (!isCanvasMode && onLegalLinkPress) {
      onLegalLinkPress(link);
    }
  };

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups((prev) => {
      const isCurrentlyExpanded = prev[groupId];
      // Only one group can be expanded at a time
      return isCurrentlyExpanded
        ? {} // Close all if clicking on expanded group
        : { [groupId]: true }; // Open only this group, close all others
    });
  };

  // Main container styles
  const mainContainerStyle = {
    backgroundColor: '#1A2737',
    paddingHorizontal: getResponsiveContainerPaddingHorizontal(),
    paddingTop: getResponsiveContainerPaddingVertical(),
    paddingBottom: 16,
    // In canvas mode, always constrain height for ScrollView to work properly
    height: isCanvasMode ? canvasHeight : undefined,
    width: isCanvasMode
      ? parseDimension(containerStyle?.width) || effectiveWidth
      : '100%',
    ...containerStyle,
  };

  // Desktop/Tablet Grid Layout
  const renderDesktopLayout = () => (
    <View style={mainContainerStyle}>
      {/* Menu Groups Section - Groups as Columns */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          marginBottom: 32,
          flexWrap: 'wrap',
          paddingHorizontal: getResponsiveContainerPaddingHorizontal(),
          paddingVertical: getResponsiveContainerPaddingVertical(),
          ...(Platform.OS === 'web' &&
            ({
              display: 'grid',
              gridTemplateColumns: `repeat(4, 1fr)`,
              gap: getResponsiveGridGap(),
              alignItems: 'start',
            } as any)),
        }}
      >
        {processedGroupMenuItems.map((group: any) => (
          <View
            key={group.id}
            style={{
              flex: Platform.OS === 'web' ? undefined : 0,
              minWidth: Platform.OS === 'web' ? 'auto' : 150,
              marginRight: Platform.OS === 'web' ? 0 : getResponsiveGridGap(),
              marginBottom: Platform.OS === 'web' ? 0 : 24,
            }}
          >
            {/* Group Title at Top */}
            <Text
              style={{
                color: '#ffffff',
                fontSize: isDesktop ? 16 : 15,
                fontWeight: 'bold',
                marginBottom: 16,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {group.title || ''}
            </Text>

            {/* Group Items Listed Vertically Below */}
            {group.items &&
              group.items.length > 0 &&
              group.items.map((item: MenuItem) => (
                <TouchableOpacity
                  key={item.id}
                  onPress={() => handleMenuItemPress(item)}
                  style={{ marginBottom: 12 }}
                  disabled={isCanvasMode}
                >
                  <Text
                    style={{
                      color: '#bdc3c7',
                      fontSize: isDesktop ? 14 : 13,
                      lineHeight: 20,
                    }}
                  >
                    {item.text || ''}
                  </Text>
                </TouchableOpacity>
              ))}
          </View>
        ))}

        {processedMenuItemsOnly.map((item: any) => (
          <View
            key={item.id}
            style={{
              flex: Platform.OS === 'web' ? undefined : 0,
              minWidth: Platform.OS === 'web' ? 'auto' : 150,
              marginRight: Platform.OS === 'web' ? 0 : getResponsiveGridGap(),
              marginBottom: Platform.OS === 'web' ? 0 : 24,
            }}
          >
            {/* Group Title at Top */}
            <Text
              style={{
                color: '#ffffff',
                fontSize: isDesktop ? 16 : 15,
                fontWeight: 'bold',
                marginBottom: 16,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {item.title || ''}
            </Text>
          </View>
        ))}
      </View>

      {/* Bottom Section */}
      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: '#34495e',
          paddingTop: 16,
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: isDesktop ? 80 : 70,
          paddingHorizontal: 40,
        }}
      >
        {/* Logo and Copyright side by side */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {/* Logo */}
          <View style={{ marginRight: 16 }}>
            {logoImage && logoImage.length > 0 ? (
              <Image
                source={{ uri: logoImage }}
                style={{ height: 40, width: 100 }}
                resizeMode="contain"
              />
            ) : (
              <Text
                style={{
                  color: '#ffffff',
                  fontSize: 18,
                  fontWeight: 'bold',
                }}
              >
                {logoData.text ?? 'LOGO'}
              </Text>
            )}
          </View>

          {/* Copyright */}
          <View>
            <Text
              style={{
                color: '#bdc3c7',
                fontSize: 12,
              }}
            >
              {bottomContent.copyright?.text || '© Company Inc. 2025'}
            </Text>
          </View>
        </View>

        {/* Legal Links */}
        <View style={{ flexDirection: 'row' }}>
          {bottomContent.legalLinks?.map((link: LegalLink, index: number) => (
            <TouchableOpacity
              key={index}
              onPress={() => handleLegalLinkPress(link)}
              style={{ marginLeft: index > 0 ? 16 : 0 }}
              disabled={isCanvasMode}
            >
              <Text
                style={{
                  color: '#bdc3c7',
                  fontSize: 12,
                  textDecorationLine: isCanvasMode ? 'none' : 'underline',
                }}
              >
                {link.text || ''}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );

  // Mobile Layout
  const renderMobileLayout = () => (
    <View style={mainContainerStyle}>
      {isCanvasMode ? (
        // Canvas mode: Use ScrollView for internal scrolling
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          {renderMobileContent()}
        </ScrollView>
      ) : (
        // Live mode: Normal layout without scroll constraint
        renderMobileContent()
      )}
    </View>
  );

  // Shared mobile content for both canvas and live modes
  const renderMobileContent = () => (
    <>
      {/* Menu Groups */}
      <View
        style={{
          marginBottom: 24,
          paddingHorizontal: getResponsiveContainerPaddingHorizontal(),
          paddingVertical: getResponsiveContainerPaddingVertical(),
          backgroundColor: mainContainerStyle.backgroundColor,
        }}
      >
        {processedGroupMenuItems.map((group: any) => {
          const isExpanded = expandedGroups[group.id] || false;
          return (
            <View
              key={group.id}
              style={{
                marginBottom: 16,
                backgroundColor: mainContainerStyle.backgroundColor,
                overflow: 'hidden',
              }}
            >
              {/* Group Title with Chevron */}
              <TouchableOpacity
                onPress={() => toggleGroupExpansion(group.id)}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingVertical: 12,
                  borderBottomWidth: 1,
                  borderBottomColor: '#34495e',
                  backgroundColor: mainContainerStyle.backgroundColor,
                }}
                disabled={isCanvasMode}
              >
                <Text
                  style={{
                    color: '#ffffff',
                    fontSize: 14,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  {group.title || ''}
                </Text>
                <Icon
                  name={
                    isExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'
                  }
                  size={20}
                  color="#ffffff"
                />
              </TouchableOpacity>

              {/* Group Items (accordion content) */}
              {group.items && group.items.length > 0 && (
                <View
                  style={{
                    backgroundColor: mainContainerStyle.backgroundColor,
                    maxHeight: isExpanded
                      ? group.items.length > 5
                        ? 200
                        : undefined
                      : 0,
                    overflow: 'hidden',
                  }}
                >
                  {group.items.length > 5 && isExpanded ? (
                    <ScrollView
                      style={{ maxHeight: 200 as any }}
                      showsVerticalScrollIndicator={true}
                      nestedScrollEnabled={true}
                    >
                      {group.items.map((item: MenuItem, itemIndex: number) => (
                        <TouchableOpacity
                          key={item.id}
                          onPress={() => handleMenuItemPress(item)}
                          style={{
                            paddingVertical: 8,
                            paddingLeft: 16,
                            borderBottomWidth:
                              itemIndex < group.items.length - 1 ? 1 : 0,
                            borderBottomColor: '#34495e',
                            backgroundColor: mainContainerStyle.backgroundColor,
                          }}
                          disabled={isCanvasMode}
                        >
                          <Text
                            style={{
                              color: '#bdc3c7',
                              fontSize: 13,
                              lineHeight: 20,
                            }}
                          >
                            {item.text || ''}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  ) : (
                    group.items.map((item: MenuItem, itemIndex: number) => (
                      <TouchableOpacity
                        key={item.id}
                        onPress={() => handleMenuItemPress(item)}
                        style={{
                          paddingVertical: 8,
                          paddingLeft: 16,
                          borderBottomWidth:
                            itemIndex < group.items.length - 1 ? 1 : 0,
                          borderBottomColor: '#34495e',
                          backgroundColor: mainContainerStyle.backgroundColor,
                        }}
                        disabled={isCanvasMode}
                      >
                        <Text
                          style={{
                            color: '#bdc3c7',
                            fontSize: 13,
                            lineHeight: 20,
                          }}
                        >
                          {item.text || ''}
                        </Text>
                      </TouchableOpacity>
                    ))
                  )}
                </View>
              )}
            </View>
          );
        })}
      </View>

      {/* Bottom Section */}
      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: '#34495e',
          paddingTop: 20,
          backgroundColor: mainContainerStyle.backgroundColor,
        }}
      >
        {/* Logo */}
        <View style={{ marginBottom: 12, alignItems: 'center' }}>
          {logoImage ? (
            <Image
              source={{ uri: logoImage }}
              style={{ height: 35, width: 90 }}
              resizeMode="contain"
            />
          ) : (
            <Text
              style={{
                color: '#ffffff',
                fontSize: 16,
                fontWeight: 'bold',
              }}
            >
              {logoData.text || 'LOGO'}
            </Text>
          )}
        </View>

        {/* Copyright */}
        <View style={{ marginBottom: 12, alignItems: 'center' }}>
          <Text
            style={{
              color: '#bdc3c7',
              fontSize: 11,
              textAlign: 'center',
            }}
          >
            {bottomContent.copyright?.text || '© Company Inc. 2025'}
          </Text>
        </View>

        {/* Legal Links */}
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {bottomContent.legalLinks?.map(
            (link: LegalLink, index: React.Key | null | undefined) => (
              <TouchableOpacity
                key={index}
                onPress={() => handleLegalLinkPress(link)}
                style={{
                  marginHorizontal: 8,
                  marginVertical: 4,
                }}
                disabled={isCanvasMode}
              >
                <Text
                  style={{
                    color: '#bdc3c7',
                    fontSize: 11,
                    textDecorationLine: isCanvasMode ? 'none' : 'underline',
                  }}
                >
                  {link.text || ''}
                </Text>
              </TouchableOpacity>
            )
          )}
        </View>
      </View>
    </>
  );

  // Render based on screen size - use mobile layout for both mobile and tablet
  return isMobile || isTablet ? renderMobileLayout() : renderDesktopLayout();
};

export default FooterLayout;
